import { useCallback, useEffect, useMemo, useState } from "react";
import { CreditCard, Gamepad2, Megaphone, Palette, Search, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import { formatRequestAmount, StatusPill } from "./AdminTransactions";

const STAT_CARDS = [
  { key: "users", label: "Members", format: "number" },
  { key: "games_on", label: "Games on", format: "number" },
  { key: "pending_reviews", label: "Pending reviews", format: "number" },
  { key: "total_deposits", label: "Total deposits", format: "money" },
  { key: "total_withdrawals", label: "Total withdrawals", format: "money" },
  { key: "member_balance", label: "Member balance", format: "money", accent: true },
];

const SHORTCUTS = [
  { href: "/admin/games", label: "Manage games", icon: Gamepad2 },
  { href: "/admin/payment-methods", label: "Payment methods", icon: CreditCard },
  { href: "/admin/communications", label: "Notice & jackpot", icon: Megaphone },
  { href: "/admin/branding", label: "Branding", icon: Palette },
];

function formatNumber(value) {
  return Number(value || 0).toLocaleString("en-US");
}

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatStat(card, stats) {
  const value = stats?.[card.key];
  return card.format === "money" ? formatMoney(value) : formatNumber(value);
}

function formatActivityTime(value) {
  if (!value) return "--:--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--:--";
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState("");
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [balanceRows, setBalanceRows] = useState([]);
  const [balanceSummary, setBalanceSummary] = useState({ wallet_total: 0, free_play_total: 0, members_with_balance: 0 });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/stats");
    setStats(data);
  }, []);

  const loadPendingReviews = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setPendingLoading(true);
    setPendingError("");
    try {
      const { data } = await api.get("/admin/pending-reviews");
      const nextRows = toPendingRows(data);
      setPendingRows(nextRows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
    } catch (err) {
      setPendingError(formatApiError(err));
    } finally {
      if (!quiet) setPendingLoading(false);
    }
  }, []);

  const openPendingReviews = () => {
    setPendingOpen(true);
    loadPendingReviews().catch(() => {});
  };

  const loadMemberBalances = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setBalanceLoading(true);
    setBalanceError("");
    try {
      const { data } = await api.get("/admin/member-balances");
      setBalanceRows(Array.isArray(data?.rows) ? data.rows : []);
      setBalanceSummary({
        wallet_total: Number(data?.wallet_total || 0),
        free_play_total: Number(data?.free_play_total || 0),
        members_with_balance: Number(data?.members_with_balance || 0),
      });
    } catch (err) {
      setBalanceError(formatApiError(err));
    } finally {
      if (!quiet) setBalanceLoading(false);
    }
  }, []);

  const openMemberBalances = () => {
    setBalanceOpen(true);
    loadMemberBalances().catch(() => {});
  };

  useEffect(() => {
    load().catch(() => setStats({}));
  }, [load]);

  useAdminAutoRefresh(() => load());
  useAdminAutoRefresh(() => (pendingOpen ? loadPendingReviews({ quiet: true }) : undefined));
  useAdminAutoRefresh(() => (balanceOpen ? loadMemberBalances({ quiet: true }) : undefined));

  const activity = stats?.recent_activity || [];

  return (
    <div className="min-h-full bg-[#08090d] px-2.5 py-3 text-white sm:px-5 sm:py-5 md:px-7">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {STAT_CARDS.map((card) => {
          const isPendingCard = card.key === "pending_reviews";
          const isBalanceCard = card.key === "member_balance";
          const isClickable = isPendingCard || isBalanceCard;
          const className = `rounded-lg border border-[#3a310f] bg-[#10100f] px-3 py-3 text-left shadow-[0_0_0_1px_rgba(0,0,0,0.3)] sm:px-4 sm:py-4 ${
            isClickable ? "transition hover:border-[#D4AF37] hover:bg-[#15130d] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60" : ""
          }`;
          const content = (
            <>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{card.label}</div>
              <div className={`mt-2 break-words font-mono text-2xl font-black leading-none tracking-normal min-[360px]:text-[28px] sm:text-[32px] ${card.accent ? "text-[#F5C400]" : "text-white"}`}>
                {stats ? formatStat(card, stats) : "-"}
              </div>
              {isPendingCard && <div className="mt-2 text-[11px] font-semibold text-[#D4AF37]">Click to view pending</div>}
              {isBalanceCard && <div className="mt-2 text-[11px] font-semibold text-[#D4AF37]">Click to view members</div>}
            </>
          );
          return isClickable ? (
            <button
              key={card.key}
              type="button"
              onClick={isPendingCard ? openPendingReviews : openMemberBalances}
              data-testid={`stat-${card.key}`}
              className={className}
            >
              {content}
            </button>
          ) : (
            <div key={card.key} data-testid={`stat-${card.key}`} className={className}>
              {content}
            </div>
          );
        })}
      </div>

      <div className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {SHORTCUTS.map(({ href, label, icon: Icon }) => (
          <a
            key={href}
            href={href}
            className="inline-flex h-10 min-w-0 items-center justify-center gap-2 rounded-full border border-white/10 bg-[#090a0f] px-3 text-sm font-bold text-white transition hover:border-[#D4AF37]/60 hover:bg-[#121214] sm:gap-3 sm:px-4"
          >
            <Icon className="h-4 w-4 text-white" />
            <span className="truncate">{label}</span>
          </a>
        ))}
      </div>

      <section className="mt-6 rounded-lg border border-[#3a310f] bg-[#10100f] p-4 md:p-5">
        <h2 className="text-sm font-bold text-white">Recent activity</h2>
        <div className="mt-4 space-y-2 font-mono text-xs">
          {activity.map((item) => (
            <div key={item.id} className="flex min-w-0 items-center gap-2 text-slate-300 sm:gap-3">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
              <span className="shrink-0 text-slate-500">{formatActivityTime(item.time)}</span>
              <span className="shrink-0 text-slate-500">{item.source || "system"}</span>
              <span className="min-w-0 truncate font-bold text-white">{item.event || "activity"}</span>
            </div>
          ))}
          {activity.length === 0 && (
            <div className="py-8 text-center font-sans text-sm text-slate-500">
              No recent activity yet.
            </div>
          )}
        </div>
      </section>

      {pendingOpen && (
        <PendingReviewsModal
          error={pendingError}
          loading={pendingLoading}
          rows={pendingRows}
          onClose={() => setPendingOpen(false)}
          onRefresh={() => loadPendingReviews()}
        />
      )}
      {balanceOpen && (
        <MemberBalancesModal
          error={balanceError}
          loading={balanceLoading}
          rows={balanceRows}
          summary={balanceSummary}
          onClose={() => setBalanceOpen(false)}
          onRefresh={() => loadMemberBalances()}
        />
      )}
    </div>
  );
}

function MemberBalancesModal({ error, loading, rows, summary, onClose, onRefresh }) {
  const [query, setQuery] = useState("");
  const filteredRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => (
      String(row.username || "").toLowerCase().includes(needle)
      || String(row.email || "").toLowerCase().includes(needle)
    ));
  }, [query, rows]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-3 backdrop-blur-sm sm:p-6" onClick={onClose}>
      <div className="max-h-[86vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-[#3a310f] bg-[#10100f] shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-4 sm:px-5">
          <div>
            <h2 className="text-xl font-black text-white">Member balances</h2>
            <p className="mt-1 text-sm text-slate-500">
              {summary.members_with_balance || 0} members with balance
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={onRefresh} className="rounded-full border border-white/10 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-white/5">
              Refresh
            </button>
            <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5" aria-label="Close member balances">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="grid gap-3 border-b border-white/10 px-4 py-4 sm:grid-cols-3 sm:px-5">
          <BalanceSummaryCard label="Wallet total" value={formatMoney(summary.wallet_total)} accent />
          <BalanceSummaryCard label="Freeplay total" value={formatMoney(summary.free_play_total)} />
          <label className="relative flex min-h-[54px] items-center rounded-xl border border-white/10 bg-black/20 px-3">
            <Search className="h-4 w-4 shrink-0 text-slate-500" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search member or email"
              className="min-w-0 flex-1 bg-transparent px-3 text-sm font-semibold text-white outline-none placeholder:text-slate-500"
            />
          </label>
        </div>
        <div className="max-h-[58vh] overflow-auto">
          {error && <div className="m-4 rounded-xl border border-red-800/50 bg-red-950/30 p-3 text-sm text-red-200">{error}</div>}
          {loading ? (
            <div className="grid min-h-[260px] place-items-center text-sm text-slate-500">Loading member balances...</div>
          ) : filteredRows.length ? (
            <table className="w-full min-w-[780px] text-sm">
              <thead className="sticky top-0 bg-[#181817] text-left text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3 text-right">Wallet</th>
                  <th className="px-4 py-3 text-right">Freeplay</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.id || row.email || row.username} className="border-t border-white/5">
                    <td className="px-4 py-3">
                      <div className="font-bold text-white">{row.username || "Member"}</div>
                      <div className="text-xs text-slate-500">{row.email || "-"}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-[#F5C400]">{formatMoney(row.wallet_balance)}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-300">{formatMoney(row.free_play_balance)}</td>
                    <td className="px-4 py-3 text-right font-mono text-white">{formatMoney(row.total_balance)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDashboardDate(row.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="grid min-h-[260px] place-items-center text-sm text-slate-500">
              {query ? "No members match that search." : "No member balances right now."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BalanceSummaryCard({ label, value, accent = false }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-xl font-black ${accent ? "text-[#F5C400]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function PendingReviewsModal({ error, loading, rows, onClose, onRefresh }) {
  const payments = rows.filter((row) => row.kind === "Payment").length;
  const payouts = rows.filter((row) => row.kind === "Payout").length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-3 backdrop-blur-sm sm:p-6" onClick={onClose}>
      <div className="max-h-[86vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-[#3a310f] bg-[#10100f] shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-4 sm:px-5">
          <div>
            <h2 className="text-xl font-black text-white">Pending reviews</h2>
            <p className="mt-1 text-sm text-slate-500">
              {payments} payments, {payouts} payouts pending
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href="/admin/payments-review" className="rounded-full border border-emerald-500/30 px-3 py-2 text-xs font-bold text-emerald-300 hover:bg-emerald-500/10">Payments</a>
            <a href="/admin/payouts-review" className="rounded-full border border-amber-500/30 px-3 py-2 text-xs font-bold text-amber-300 hover:bg-amber-500/10">Payouts</a>
            <button type="button" onClick={onRefresh} className="rounded-full border border-white/10 px-3 py-2 text-xs font-bold text-slate-200 hover:bg-white/5">
              Refresh
            </button>
            <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-slate-300 hover:bg-white/5" aria-label="Close pending reviews">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="max-h-[66vh] overflow-auto">
          {error && <div className="m-4 rounded-xl border border-red-800/50 bg-red-950/30 p-3 text-sm text-red-200">{error}</div>}
          {loading ? (
            <div className="grid min-h-[260px] place-items-center text-sm text-slate-500">Loading pending reviews...</div>
          ) : rows.length ? (
            <table className="w-full min-w-[860px] text-sm">
              <thead className="sticky top-0 bg-[#181817] text-left text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Member</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-white/5">
                    <td className="px-4 py-3">
                      <span className={`rounded-md border px-2 py-1 text-xs font-bold ${row.kind === "Payout" ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>
                        {row.kind}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-white">{row.username || "-"}</div>
                      <div className="text-xs text-slate-500">{row.email || "-"}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-[#F5C400]">{formatRequestAmount(row)}</td>
                    <td className="px-4 py-3 text-slate-300">{pendingMethod(row)}</td>
                    <td className="px-4 py-3"><StatusPill status={row.status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDashboardDate(row.created_at)}</td>
                    <td className="max-w-[260px] truncate px-4 py-3 text-xs text-slate-500">{row.note || row.admin_note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="grid min-h-[260px] place-items-center text-sm text-slate-500">No pending reviews right now.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function toPendingRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    kind: row.type === "redeem" ? "Payout" : "Payment",
  }));
}

function pendingMethod(row) {
  return row.payment_platform || row.payment_provider || row.payment_method || row.method || row.game_id || "-";
}

function formatDashboardDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US");
}
